import { buildAggregateGroupRowKey } from "../../services/beefAiPricing";
import {
  discountFilterChoices,
  formatProviderOptionLabel,
} from "./constants";
import {
  monthlyByProductChartSubtitle,
  monthlyByProductChartTitle,
  monthlyByProvinceChartSubtitle,
  monthlyByProvinceChartTitle,
  monthlyByRetailerChartSubtitle,
  monthlyByRetailerChartTitle,
  quarterlyChartSubtitle,
  quarterlyChartTitle,
  snapshotProvinceChartDescription,
  snapshotProvinceChartTitle,
  snapshotRetailerChartDescription,
  snapshotRetailerChartTitle,
} from "./pricingReportChartCopy";

function formatProvidersForPrintHeader(selectedProvidersInclude) {
  if (!selectedProvidersInclude?.length) {
    return "All providers";
  }
  return selectedProvidersInclude.map((provider) => formatProviderOptionLabel(provider)).join(", ");
}

function formatDiscountLabel(isDiscounted) {
  const choice = discountFilterChoices.find((item) => item.value === isDiscounted);
  return choice?.label ?? discountFilterChoices[0].label;
}

function coverUrlLookupFrom(coverUrlByRowKey) {
  if (coverUrlByRowKey instanceof Map) {
    return coverUrlByRowKey;
  }
  return new Map(Object.entries(coverUrlByRowKey || {}));
}

export function buildPricingReportPrintCardRows(rows, coverUrlByRowKey) {
  const lookup = coverUrlLookupFrom(coverUrlByRowKey);
  return (rows || []).map((row) => {
    const rowKey = buildAggregateGroupRowKey(row?.drilldown);
    return {
      ...row,
      coverUrl: lookup.get(rowKey) || null,
    };
  });
}

/**
 * @param {object} params
 */
export function buildPricingReportPrintSnapshot({
  generatedAt = new Date(),
  startDateForApi,
  endDateForApi,
  dateFromDisplay,
  dateToDisplay,
  productLabel,
  selectedProvince,
  retailerLabel,
  summaryCardSelectedRetailers = [],
  selectedProvidersInclude,
  isDiscounted,
  quarterlyLookbackYears,
  hasSelectedProduct,
  selectedProductCount = 0,
  productSnapshotDisplayName,
  showQuarterlyKpis,
  quarterlySummaryStats,
  chartImagesByKey,
  averageUnitPriceTableRows,
  aggregatePage,
  aggregateRowsPerPage,
  aggregateTotalGroupCount,
  tableTitle,
  tableColumns,
  resultsView = "table",
  coverUrlByRowKey = null,
  placeholderImageUrl = null,
  retailerLogoAssetId = null,
  retailerLogoAlt = "Retailer logo",
  monthlyByProductChartGranularity = "month",
  productChartCombinedView = false,
  retailerChartCombinedView = false,
  provinceChartCombinedView = false,
}) {
  const logoUrl = retailerLogoAssetId
    ? `${process.env.REACT_APP_API_URL}/assets/${retailerLogoAssetId}?key=system-medium-contain`
    : null;
  const productSnapshotSections = [];
  const pricingChartSections = [];

  if (chartImagesByKey["snapshot-retailer"]) {
    productSnapshotSections.push({
      key: "snapshot-retailer",
      title: snapshotRetailerChartTitle(productSnapshotDisplayName),
      description: snapshotRetailerChartDescription(productSnapshotDisplayName, {
        dateFrom: dateFromDisplay || startDateForApi,
        dateTo: dateToDisplay || (endDateForApi ? endDateForApi.slice(0, 10) : ""),
      }),
      imageDataUrl: chartImagesByKey["snapshot-retailer"],
    });
  }

  if (chartImagesByKey["snapshot-province"]) {
    productSnapshotSections.push({
      key: "snapshot-province",
      title: snapshotProvinceChartTitle(productSnapshotDisplayName),
      description: snapshotProvinceChartDescription(productSnapshotDisplayName, {
        dateFrom: dateFromDisplay || startDateForApi,
        dateTo: dateToDisplay || (endDateForApi ? endDateForApi.slice(0, 10) : ""),
      }),
      imageDataUrl: chartImagesByKey["snapshot-province"],
    });
  }

  const printChartDateContext = {
    dateFrom: dateFromDisplay || startDateForApi,
    dateTo: dateToDisplay || (endDateForApi ? endDateForApi.slice(0, 10) : ""),
  };

  if (chartImagesByKey["monthly-product"]) {
    pricingChartSections.push({
      key: "monthly-product",
      title: monthlyByProductChartTitle(monthlyByProductChartGranularity),
      subtitle: monthlyByProductChartSubtitle(
        hasSelectedProduct,
        monthlyByProductChartGranularity,
        printChartDateContext,
        productChartCombinedView,
        selectedProductCount
      ),
      imageDataUrl: chartImagesByKey["monthly-product"],
    });
  }

  if (chartImagesByKey["monthly-retailer"]) {
    pricingChartSections.push({
      key: "monthly-retailer",
      title: monthlyByRetailerChartTitle(),
      subtitle: monthlyByRetailerChartSubtitle(retailerChartCombinedView, printChartDateContext),
      imageDataUrl: chartImagesByKey["monthly-retailer"],
    });
  }

  if (chartImagesByKey["monthly-province"]) {
    pricingChartSections.push({
      key: "monthly-province",
      title: monthlyByProvinceChartTitle(),
      subtitle: monthlyByProvinceChartSubtitle(provinceChartCombinedView, printChartDateContext),
      imageDataUrl: chartImagesByKey["monthly-province"],
    });
  }

  if (chartImagesByKey.quarterly) {
    pricingChartSections.push({
      key: "quarterly",
      title: quarterlyChartTitle(),
      subtitle: quarterlyChartSubtitle(quarterlyLookbackYears),
      imageDataUrl: chartImagesByKey.quarterly,
    });
  }

  const includeQuarterlyLookbackNote = false;

  const pageStart = aggregatePage * aggregateRowsPerPage + 1;
  const pageEnd = Math.min((aggregatePage + 1) * aggregateRowsPerPage, aggregateTotalGroupCount);
  const isCardView = resultsView === "card";
  const footerNote = isCardView
    ? `Cards show page ${aggregatePage + 1} (items ${pageStart}–${pageEnd} of ${aggregateTotalGroupCount} groups). Export to CSV for all filtered data.`
    : `Table shows page ${aggregatePage + 1} (rows ${pageStart}–${pageEnd} of ${aggregateTotalGroupCount} groups). Export to CSV for all filtered data.`;

  return {
    generatedAt,
    header: {
      generatedAt,
      dateFromDisplay: dateFromDisplay || startDateForApi,
      dateToDisplay: dateToDisplay || (endDateForApi ? endDateForApi.slice(0, 10) : ""),
      productLabel: productLabel || "All products",
      provinceLabel: selectedProvince?.label ?? "All provinces",
      retailerLabel: retailerLabel || "All retailers",
      providersLabel: formatProvidersForPrintHeader(selectedProvidersInclude),
      discountLabel: formatDiscountLabel(isDiscounted),
      includeQuarterlyLookbackNote,
      quarterlyLookbackYears,
      logoUrl,
      logoAlt: retailerLogoAlt,
    },
    showQuarterlyKpis,
    quarterlySummaryStats: showQuarterlyKpis ? quarterlySummaryStats : null,
    summaryCardSelectedProvince: selectedProvince ?? null,
    summaryCardSelectedRetailers: summaryCardSelectedRetailers ?? [],
    productSnapshotSections,
    pricingChartSections,
    resultsView: isCardView ? "card" : "table",
    table: isCardView
      ? null
      : {
          title: tableTitle,
          rows: averageUnitPriceTableRows,
          columns: tableColumns,
          footerNote,
        },
    cards: isCardView
      ? {
          title: tableTitle,
          rows: buildPricingReportPrintCardRows(averageUnitPriceTableRows, coverUrlByRowKey),
          footerNote,
          placeholderImageUrl,
        }
      : null,
  };
}
